import React, { useState } from "react";
import { Save, Loader2, ShieldAlert } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Separator } from "../../components/ui/separator";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { Badge } from "../../components/ui/badge";
import { useToast } from "../../contexts/ToastContext";

export default function SystemConfigPage() {
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({
    rollbackRateThreshold: "5",
    canaryPct: "5",
    phase2Pct: "25",
    signedUrlTtl: "10",
    maxFirmwareSize: "300",
    cloudwatchRetentionDays: "30",
  });

  const handleChange = (key, val) => setConfig((prev) => ({ ...prev, [key]: val }));

  const handleSave = async () => {
    setSaving(true);
    await new Promise((r) => setTimeout(r, 800)); // TODO: PUT /admin/config
    setSaving(false);
    toast({ title: "Config saved", description: "Changes will take effect on the next rollout.", variant: "success" });
  };

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-slate-900">System Configuration</h2>
        <p className="text-sm text-slate-500 mt-0.5">
          Global parameters for OTA rollout behaviour and infrastructure limits.
        </p>
      </div>

      <Alert variant="warning">
        <ShieldAlert className="h-4 w-4" />
        <AlertDescription>
          Changes here affect live fleet behaviour. Lowering the rollback threshold may pause in-progress rollouts.
        </AlertDescription>
      </Alert>

      {/* Rollout config */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Rollout thresholds</CardTitle>
          <CardDescription>Controls phased rollout behaviour and auto-pause thresholds.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <ConfigRow
            label="Rollback Rate Threshold (%)"
            desc="A rollout is withdrawn automatically when the reported failure rate reaches this value."
            id="rr-threshold"
            value={config.rollbackRateThreshold}
            onChange={(v) => handleChange("rollbackRateThreshold", v)}
            suffix="%"
          />
          <Separator />
          <ConfigRow
            label="Canary Phase (%)"
            desc="Percentage of target segment for the first rollout phase."
            id="canary-pct"
            value={config.canaryPct}
            onChange={(v) => handleChange("canaryPct", v)}
            suffix="%"
          />
          <Separator />
          <ConfigRow
            label="Phase 2 Target (%)"
            desc="Percentage of target segment for the second rollout phase."
            id="phase2-pct"
            value={config.phase2Pct}
            onChange={(v) => handleChange("phase2Pct", v)}
            suffix="%"
          />
        </CardContent>
      </Card>

      {/* S3 / delivery config */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">S3 Delivery</CardTitle>
          <CardDescription>Firmware payload delivery settings. No CloudFront — direct S3 pre-signed URLs.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <ConfigRow
            label="Pre-signed URL TTL (minutes)"
            desc="Time window a Mobile client has to download after receiving the URL. Recommended: 10 min."
            id="url-ttl"
            value={config.signedUrlTtl}
            onChange={(v) => handleChange("signedUrlTtl", v)}
            suffix="min"
          />
          <Separator />
          <ConfigRow
            label="Max Firmware Size (KB)"
            desc="Upload is rejected server-side if the .bin file exceeds this size."
            id="max-fw"
            value={config.maxFirmwareSize}
            onChange={(v) => handleChange("maxFirmwareSize", v)}
            suffix="KB"
          />
        </CardContent>
      </Card>

      {/* Observability */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Observability</CardTitle>
          <CardDescription>CloudWatch and CloudTrail retention settings.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <ConfigRow
            label="CloudWatch Log Retention (days)"
            desc="Logs older than this are archived to S3 (IA tier) automatically."
            id="cw-retention"
            value={config.cloudwatchRetentionDays}
            onChange={(v) => handleChange("cloudwatchRetentionDays", v)}
            suffix="days"
          />
        </CardContent>
      </Card>

      {/* Service catalogue summary */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Deployed Service Catalogue</CardTitle>
          <CardDescription>INC 1 live services. INC 2 infrastructure provisioned but dormant.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-2 text-xs">
            {[
              ["Cognito", "INC 1", "success"],
              ["KMS", "INC 1", "success"],
              ["API Gateway", "INC 1", "success"],
              ["Rollout service", "INC 1", "success"],
              ["Lambda (5 handlers)", "INC 1", "success"],
              ["S3 Firmware", "INC 1", "success"],
              ["DynamoDB Core", "INC 1", "success"],
              ["EventBridge", "INC 1", "success"],
              ["Notification service", "INC 1", "success"],
              ["CloudWatch", "INC 1", "success"],
              ["S3 Device Logs", "INC 2", "warning"],
              ["S3 App Logs", "INC 2", "warning"],
              ["DynamoDB SessionIndex", "INC 2", "warning"],
              ["Lambda Log Ingest", "INC 2", "warning"],
              ["Athena + Glue", "INC 1", "success"],
              ["QuickSight", "INC 2", "muted"],
            ].map(([svc, inc, variant]) => (
              <div key={svc} className="flex items-center justify-between px-2 py-1.5 rounded border bg-slate-50">
                <span className="text-slate-700">{svc}</span>
                <Badge variant={variant} className="text-[10px]">{inc}</Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Button onClick={handleSave} disabled={saving}>
        {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
        Save Changes
      </Button>
    </div>
  );
}

function ConfigRow({ label, desc, id, value, onChange, suffix }) {
  return (
    <div className="flex items-start justify-between gap-6">
      <div className="flex-1">
        <Label htmlFor={id}>{label}</Label>
        <p className="text-xs text-slate-400 mt-0.5">{desc}</p>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <Input
          id={id}
          type="number"
          className="w-24 h-8 text-sm"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
        <span className="text-xs text-slate-500">{suffix}</span>
      </div>
    </div>
  );
}
